import {
  PropsWithChildren,
  createContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import * as Sentry from '@sentry/react';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';

import { css, Global } from '@emotion/react';
import axios from 'axios';
import { notifySentry } from 'utils/error/notifySentry';
import baseTheme from '../../ui/theme/theme';
import i18n from '../../config/i18n';

export type VendorConfig =
  typeof import('../../../public/vendor/reno/config.json');

// Context
export const VendorContext = createContext<VendorConfig>({} as any);

const GlobalStyles = css`
  /*
    This will hide the focus indicator if the element receives focus    via the mouse,
    but it will still show up on keyboard focus.
  */
  .js-focus-visible :focus:not([data-focus-visible-added]) {
    outline: 0 0 0 3px white;
    box-shadow: none;
  }

  .js-focus-visible :focus:not(.focus-visible),
  .js-focus-visible :focus:not(.focus-visible) + [data-focus] {
    outline: 0 0 0 3px white;
    box-shadow: 0 0 0 3px white;
  }

  .adress-select__control:hover {
    border-color: 'red' !important;
  }

  *:focus {
    outline: 0 !important;
    border-color: 0 !important;
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0) !important;
  }
`;

const vendorId = process.env.REACT_APP_VENDOR;

// Context provider
export const VendorProvider = ({ children }: PropsWithChildren<ReactNode>) => {
  const [vendorConfig, setVendorConfig] = useState<any>();
  const [vendorTheme, setVendorTheme] = useState<typeof baseTheme | undefined>(
    undefined
  );
  const [vendorCss, setVendorCss] = useState<string>();
  const isLoading = !vendorConfig || !vendorTheme || !vendorCss;

  useEffect(() => {
    // load vendor data
    axios
      .get(`vendor/${vendorId}/config.json?${new Date().getTime()}`)
      .then((res) => {
        const config = res.data;
        // set config
        setVendorConfig(config);
        // update language
        i18n.changeLanguage(config.language);
        // update theme
        setVendorTheme(extendTheme(config.theme, baseTheme));
        // update fonts
      })
      .catch((err) => notifySentry(err));

    // load vendor css
    axios
      .get(`vendor/${vendorId}/fonts.css?${new Date().getTime()}`)
      .then((res) => {
        setVendorCss(res.data);
      })
      .catch((err) => notifySentry(err));
  }, []);

  // do not render if still
  if (isLoading) return null;

  // set sentry context
  Sentry.setContext('vendorConfig', vendorConfig);

  return (
    <VendorContext.Provider value={vendorConfig}>
      {vendorTheme && (
        <ChakraProvider theme={vendorTheme}>
          <Global styles={GlobalStyles} />
          {vendorCss && <Global styles={css(vendorCss)} />}
          {children}
        </ChakraProvider>
      )}
    </VendorContext.Provider>
  );
};
